import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ClientTable = ({ 
  clients, 
  onEdit, 
  onViewHistory, 
  onToggleStatus, 
  onAssignPlan,
  onViewEvaluation,
  onSort,
  sortField,
  sortDirection 
}) => {
  const [selectedClients, setSelectedClients] = useState([]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'inactive': return 'text-muted-foreground bg-muted/30';
      case 'suspended': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/30';
    }
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  const formatLastTraining = (lastTraining) => {
    if (!lastTraining) return 'Never';
    
    const date = new Date(lastTraining);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date?.toLocaleDateString();
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedClients(clients?.map(client => client?.id));
    } else {
      setSelectedClients([]);
    }
  };

  const handleSelectClient = (clientId, checked) => {
    if (checked) {
      setSelectedClients([...selectedClients, clientId]);
    } else {
      setSelectedClients(selectedClients?.filter(id => id !== clientId));
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ArrowUpDown';
    return sortDirection === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const tableHeaders = [
    { key: 'select', label: '', sortable: false },
    { key: 'photo', label: '', sortable: false },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'age', label: 'Age', sortable: true },
    { key: 'rfidId', label: 'RFID ID', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'activePlans', label: 'Active Plans', sortable: true },
    { key: 'lastTraining', label: 'Last Training', sortable: true },
    { key: 'actions', label: 'Actions', sortable: false }
  ];

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden">
      {/* Bulk Actions Bar */}
      {selectedClients?.length > 0 && (
        <div className="bg-primary/5 border-b border-border p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-foreground">
                {selectedClients?.length} client{selectedClients?.length > 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                iconPosition="left"
                iconSize={16}
              >
                Bulk Assign Plan
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="UserX"
                iconPosition="left"
                iconSize={16}
              >
                Bulk Deactivate
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedClients([])}
                iconName="X"
                iconSize={16}
              >
                Clear
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              {tableHeaders?.map((header) => (
                <th
                  key={header?.key}
                  className={`px-6 py-4 text-left text-sm font-medium text-foreground ${
                    header?.sortable ? 'cursor-pointer hover:bg-muted/50' : ''
                  }`}
                  onClick={header?.sortable ? () => onSort(header?.key) : undefined}
                >
                  <div className="flex items-center space-x-2">
                    {header?.key === 'select' ? (
                      <input
                        type="checkbox"
                        checked={selectedClients?.length === clients?.length && clients?.length > 0}
                        onChange={(e) => handleSelectAll(e?.target?.checked)}
                        className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                      />
                    ) : (
                      <>
                        <span>{header?.label}</span>
                        {header?.sortable && (
                          <Icon 
                            name={getSortIcon(header?.key)} 
                            size={16} 
                            className="text-muted-foreground" 
                          />
                        )}
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {clients?.map((client) => (
              <tr key={client?.id} className="hover:bg-muted/20 transition-colors duration-150">
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedClients?.includes(client?.id)}
                    onChange={(e) => handleSelectClient(client?.id, e?.target?.checked)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                </td>
                
                <td className="px-6 py-4">
                  <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                    {client?.photo ? (
                      <Image
                        src={client?.photo}
                        alt={`${client?.name} profile photo`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="User" size={16} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </td>
                
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-foreground">{client?.name}</p>
                    {client?.textIdentifier && (
                      <p className="text-sm text-muted-foreground">{client?.textIdentifier}</p>
                    )}
                  </div>
                </td>
                
                <td className="px-6 py-4 text-foreground">
                  {calculateAge(client?.dateOfBirth)}
                </td>
                
                <td className="px-6 py-4">
                  <code className="text-sm bg-muted/30 px-2 py-1 rounded text-foreground">
                    {client?.rfidId}
                  </code>
                </td>
                
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(client?.status)}`}>
                    {client?.status?.charAt(0)?.toUpperCase() + client?.status?.slice(1)}
                  </span>
                </td>
                
                <td className="px-6 py-4 text-foreground">
                  {client?.activePlans || 0}
                </td>
                
                <td className="px-6 py-4 text-foreground">
                  {formatLastTraining(client?.lastTraining)}
                </td>
                
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(client)}
                      iconName="Edit"
                      iconSize={16}
                      className="p-2"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewHistory(client)}
                      iconName="History"
                      iconSize={16}
                      className="p-2"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAssignPlan(client)}
                      iconName="FileText"
                      iconSize={16}
                      className="p-2"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewEvaluation(client)}
                      iconName="BarChart3"
                      iconSize={16}
                      className="p-2"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onToggleStatus(client)}
                      iconName={client?.status === 'active' ? 'UserX' : 'UserCheck'}
                      iconSize={16}
                      className={`p-2 ${client?.status === 'active' ? 'text-warning hover:text-warning' : 'text-success hover:text-success'}`}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {clients?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No clients found</h3>
          <p className="text-muted-foreground">
            Add your first client to get started with training management.
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientTable;